import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import Classnames from 'classnames';
import Moment from 'moment';

import Button from '../../components/common/button/Button';
import Card from '../../components/common/card/Card';
import Loader from '../../components/common/loader/Loader';
import Icon from '../../components/common/icon/Icon';
import Spacer from '../../components/common/spacer/Spacer';
import Pagination from '../../components/common/pagination/Pagination';
import ReviewCard from '../../app-utils/card/ReviewCard';
import ErrorScreen from '../../components/derived/error/ErrorScreen';
import WriteAReview from '../../components/derived/modal/WriteAReview';

import Api from '../../../utils/api';

require('./style.scss');

const RATING_ICON_MAPPING = new Map();
RATING_ICON_MAPPING.set(1, '/static/images/rating_1star.png');
RATING_ICON_MAPPING.set(2, '/static/images/rating_2star.png');
RATING_ICON_MAPPING.set(3, '/static/images/rating_3star.png');
RATING_ICON_MAPPING.set(4, '/static/images/rating_4star.png');
RATING_ICON_MAPPING.set(5, '/static/images/rating_5star.png');

const REVIEW_COUNT = 3;

export default class Reviews extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      reviewCount: 0,
      reviews: null,
      metrics: {},
      selectedRating: null,
      showReviewModal: false,
      showRatingDropDownModal: false,
    };
  }

  componentDidMount() {
    const { profile, params } = this.props;
    this.fetchReviews(profile.code, params.pageId || 1,
      this.state.selectedRating || undefined);
    this.fetchReviewMetrics(profile.code);
  }

  componentWillReceiveProps(nextProps) {
    const pageId = nextProps.params.pageId;
    if (pageId) {
      this.fetchReviews(nextProps.profile.code, pageId,
        this.state.selectedRating || undefined);
    }
  }

  setSelectedRating(selectedRating) {
    this.setState({ showRatingDropDownModal: false });

    if (selectedRating !== this.state.selectedRating) {
      this.setState({ selectedRating });
      this.props.router.push(`/${this.props.params.partnerSlug}/reviews`);
    }
  }

  fetchReviews(code, page, rating = undefined) {
    this.setState({ reviews: null });

    if (!code) {
      return;
    }

    Api.reviews(code, page, rating, REVIEW_COUNT).then(response =>
      this.setState({ reviewCount: response.count, reviews: response.results }));
  }

  fetchReviewMetrics(code) {
    if (!code) {
      return;
    }

    Api.reviewMetrics(code).then(response => this.setState({
      metrics: {
        reviewCount: response.count_all,
        count5Star: response.count_5_star,
        count4Star: response.count_4_star,
        count3Star: response.count_3_star,
        count2Star: response.count_2_star,
        count1Star: response.count_1_star,
      },
    }));
  }

  renderSmiley(rating, count, selectedRating) {
    return (
      <span
        key={rating || 'all'}
        className={Classnames('review-rating-smiley-box', { selected: rating === selectedRating })}
        onClick={() => this.setSelectedRating(rating)}
      >
        {rating ?
          <span className="review-rating-icon">
            <img
              alt={`Rating ${rating}`}
              src={RATING_ICON_MAPPING.get(rating)}
            />
          </span> : 'All' }
        <span className="review-rating-count">{rating ? count || 0 : this.state.reviewCount}</span>
      </span>);
  }

  renderRatingBar() {
    const selectedRating = this.state.selectedRating;

    return (
      <div id="rating-bar">
        <div className="hidden-md hidden-lg rating-dropdown">
          <div
            className="review-rating-smiley-box selected"
            onClick={() => this.setState({
              showRatingDropDownModal: !this.state.showRatingDropDownModal,
            })}
          >
            {this.renderSmiley(
              selectedRating,
              this.state.metrics[`count${selectedRating}Star`],
              selectedRating)} <Icon name="caret-down" />
          </div>

          {this.state.showRatingDropDownModal ?
            <div className="review-rating-dropdown">
              {[null, 5, 4, 3, 2, 1].map(number =>
                this.renderSmiley(
                  number,
                  this.state.metrics[`count${number}Star`], this.state.selectedRating))}
            </div> : null}
        </div>

        <div className="hidden-sm hidden-xs">
          <span
            className={
              Classnames(
                'review-rating-smiley-box',
                { selected: this.state.selectedRating === null })}
            onClick={() => this.setSelectedRating(null)}
          >
            <span className="review-rating-count">
              All ({this.state.metrics.reviewCount})
            </span>
          </span>

          {[5, 4, 3, 2, 1].map(number =>
            this.renderSmiley(number, this.state.metrics[`count${number}Star`], this.state.selectedRating))}
        </div>
      </div>
    );
  }

  renderReviewCard(review) {
    return (
      <ReviewCard
        key={review.id}
        backgroundColor="#ffffff"
        avatar={
          <img
            alt={review.customer_name}
            src={review.customer_image_url || '/static/images/user_default.png'}
          />}
        createdAt={Moment(review.created_at)}
        username={review.customer_name}
        rating={review.rating}
        review={review.content}
        reply={review.reply ?
          <ReviewCard.Reply
            reply={review.reply.content}
            username={this.state.storeName}
          /> : null}
        showReply
      />);
  }

  renderReviews() {
    return (
      <div id="reviews">
        <Card>
          <Card.Content>
            <Card.Header
              actions={
                <Button
                  mobileFriendly
                  leftIcon={<Icon name="pencil" />}
                  label="Write a review"
                  backgroundColor="#cccccc"
                  color="black"
                  onClick={() => this.setState({ showReviewModal: true })}
                />}
            >
              {this.renderRatingBar()}
            </Card.Header>

            <Card.List>
              {this.state.reviews === null ?
                <Loader open /> :
                this.state.reviews.map(review => this.renderReviewCard(review))}
            </Card.List>
          </Card.Content>
        </Card>

        <Spacer size={2} />
        <Pagination
          totalPages={_.ceil((this.state.reviewCount || 1) / REVIEW_COUNT)}
          currentPage={parseInt(this.props.params.pageId, 10) || 1}
          urlPrefix={`/${this.props.params.partnerSlug}/reviews`}
        />
      </div>
    );
  }

  render() {
    const { profile } = this.props;
    const reviewCount = _.size(this.state.reviews);
    let cards = null;

    if (this.state.reviews === null ||
        reviewCount > 0 ||
        this.state.selectedRating !== null
    ) {
      cards = (
        <div className="container-fluid">
          {this.renderReviews()}
        </div>);
    } else {
      cards = (
        <ErrorScreen
          media={<img role="presentation" src="/static/images/review.png" />}
          title="Be the first one to review"
          helpText={`Stay updated with ${profile.store_name}<br />` +
                      'through m.Paani Shop Smart App'}
          actions={
            <Button
              label="Write a review"
              onClick={() => this.setState({ showReviewModal: true })}
              large
            />}
        />
      );
    }

    return (
      <main id="reviews-body">
        <WriteAReview
          code={profile.code}
          open={this.state.showReviewModal}
          onClose={() => this.setState({ showReviewModal: false })}
        />

        <Spacer size={2} />

        <div id="content">
          {cards}
        </div>

        <Spacer size={2} />

      </main>
    );
  }
}

Reviews.propTypes = {
  params: PropTypes.shape({
    pageId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    partnerSlug: PropTypes.string,
  }),
};



// WEBPACK FOOTER //
// ./app/pages/reviews/Reviews.jsx